/* eslint-disable react/jsx-props-no-spreading */
import type { FlagCountryCode } from 'components/Flag';
import { Chip } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { MeasureFormat } from 'components/measure-unit/MeasureFormat';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { MeasureComparison } from 'components/MeasureComparison';
import React, { createContext, ReactElement, useContext } from 'react';
import { Flag } from 'components/Flag';
import { Measure } from 'services/model/model.service';
import { addImageNotAvailable } from 'services/product-data.service';
import { getChipSxStylingForLevel } from 'styles/utils';
import { cn } from 'utils/classNamesTailwind';

const RowClickableContext = createContext(false);

function ListContainer({
  className = '',
  children = undefined,
  header = undefined,
  columnWidths = undefined,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  header?: ReactElement;
  columnWidths?: string[];
}) {
  return (
    <table className={cn(className, columnWidths ? 'table-fixed' : 'table-auto', 'w-full gap-y-4')} {...props}>
      {columnWidths && (
        <colgroup>
          <col />
          {columnWidths.map((width, index) => (
            <col key={index} className={width} />
          ))}
        </colgroup>
      )}
      {header && <thead>{header}</thead>}
      <tbody>{children}</tbody>
    </table>
  );
}

function ListHeaderRow({ className = '', children = undefined, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <tr className={cn('text-[10px] text-ash-900 border-b border-snow-200', className)} {...props}>
      {children}
    </tr>
  );
}

function ListHeaderCell({ className = '', children = undefined, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <th className={cn('font-bold pl-6 py-2 text-left whitespace-nowrap', className)} {...props}>
      {children}
    </th>
  );
}

function ListRow({
  className = '',
  children = undefined,
  fixedHeight = true,
  onClick,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { fixedHeight?: boolean }) {
  return (
    <tr
      className={cn(
        'divide-gray-300 last:border-b-0 border-b border-snow-200 group/row',
        fixedHeight ? ' h-16' : '',
        onClick ? 'hover:bg-denim-50 hover:cursor-pointer' : '',
        className
      )}
      onClick={onClick}
      {...props}
    >
      <RowClickableContext.Provider value={!!onClick}>{children}</RowClickableContext.Provider>
    </tr>
  );
}

function NameCell({
  countryCode = undefined,
  icon = undefined,
  imgSrc = undefined,
  name,
  color = '#877059',
  hideColorBar = false,
  imageClassName = 'w-12',
}: {
  countryCode?: FlagCountryCode;
  icon?: React.ReactNode;
  imgSrc?: string;
  name: string | ReactElement;
  color?: string;
  hideColorBar?: boolean;
  imageClassName?: string;
}) {
  const rowClickable = useContext(RowClickableContext);
  const hasLeadingElement = !hideColorBar || !!countryCode || !!icon || !!imgSrc;

  return (
    <td className={cn('w-full max-h-16', rowClickable && 'cursor-pointer')}>
      <div className="flex items-center ">
        {!hideColorBar && <div className="h-12 mr-4 w-1" style={{ backgroundColor: color }} />}
        {countryCode && <Flag countryCode={countryCode} tooltip={name as string} />}
        {icon && <div>{icon}</div>}
        {imgSrc && (
          <img src={imgSrc} onError={addImageNotAvailable} className={imageClassName} alt="Product Thumbnail" />
        )}
        <p
          className={cn(
            'text-md break-words line-clamp-2',
            hasLeadingElement && 'pl-2',
            rowClickable ? 'underline group-hover/row:text-denim-500 group-hover/row:font-semibold' : ''
          )}
        >
          {name}
        </p>
      </div>
    </td>
  );
}

function MetricCell({
  measure = undefined,
  label = undefined,
  value = undefined,
  comparisonValue = 0,
  unit = undefined,
  comparisonActive = false,
  size = 'sm',
  signedPositive = false,
  hideMeasureLabel = false,
  interpretation = 1,
  boldValue = true,
  valueClassName = undefined,
}: {
  measure?: Measure;
  label?: string;
  value?: number;
  comparisonValue?: number;
  unit?: MeasureUnitCode;
  comparisonActive?: boolean;
  size?: 'sm' | 'lg';
  signedPositive?: boolean;
  hideMeasureLabel?: boolean;
  interpretation?: number;
  boldValue?: boolean;
  valueClassName?: string;
}): ReactElement {
  const intl = useIntl();

  if (value === undefined || unit === undefined) {
    return <td className="pl-6">–</td>;
  }

  const valueRow = (
    <div
      className={cn(
        size === 'sm' ? 'text-sm' : 'text-base',
        boldValue && 'font-bold',
        'flex flex-row items-center gap-2'
      )}
    >
      <p className={cn('whitespace-nowrap', valueClassName)}>
        {value === Infinity ? (
          <Chip
            label={intl.formatMessage({ id: 'common.new', defaultMessage: 'New' })}
            size="small"
            sx={getChipSxStylingForLevel('Information')}
            className="font-normal"
          />
        ) : (
          <MeasureFormat value={value} unit={unit} signedPositive={signedPositive} />
        )}
      </p>
      {comparisonActive && (
        <MeasureComparison
          value={value}
          comparisonValue={comparisonValue ?? 0}
          unit={MeasureUnitCode.QUANTITY}
          interpretation={interpretation}
        />
      )}
    </div>
  );

  if (hideMeasureLabel) {
    return <td className="pl-6">{valueRow}</td>;
  }

  return (
    <td className="pl-6">
      <div className="grid grid-rows-2">
        <div className={`${size === 'sm' ? 'text-xss' : 'text-xs'} flex items-center-safe`}>
          <span className="inline-block whitespace-nowrap leading-snug">
            {measure?.metric.name ?? ''}
            {label ? <FormattedMessage id={label} /> : ''}
          </span>
        </div>
        {valueRow}
      </div>
    </td>
  );
}

export { ListContainer, ListRow, ListHeaderRow, ListHeaderCell, MetricCell, NameCell };
